import { useRef, useEffect, useMemo } from "react";

import { Divider } from "primereact/divider";

import type { CodeSubHintResponse } from "sj-ui-toolkit/src/client";

import { useAiHints } from "../../contexts/SandboxHooks";

import S from "./HintsPanel.module.scss";

import AspenMessage from "./AspenMessage";

const NoHintsMessage =
  'To get hints on this lesson, please make a code submission and click the "Need a hint?" button from the Tests tab.';

interface HintsPanelProps {
  openDrawer?: () => void;
}

const HintsPanel = ({ openDrawer }: HintsPanelProps) => {
  const { hints, hintsLoading, hintsError, highlightedHint } = useAiHints();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Turn the hints into an array of aiHint messages, sorted by timestamp
  const aspenMessages = useMemo(() => {
    if (!Array.isArray(hints)) return [];

    // Hints normally nest their data under `hint`, but some responses put it
    // directly on the record — keep the fallbacks so both shapes render.
    type LegacyHint = CodeSubHintResponse & {
      text?: string;
      feedback?: CodeSubHintResponse["hint"]["feedback"];
      timestamp?: string;
    };

    return hints
      .map((h: LegacyHint) => ({
        runId: h.submissionId,
        text: h.hint?.text || h.text || "",
        feedback: h.hint?.feedback || h.feedback,
        timestamp: h.createdAt || h.timestamp,
      }))
      .sort((a, b) => {
        const da = new Date(a.timestamp || 0).getTime();
        const db = new Date(b.timestamp || 0).getTime();
        return da - db; // Oldest first for chat-like display
      });
  }, [hints]);

  // Open the panel & scroll to the bottom when a new hint is added
  useEffect(() => {
    if (scrollRef.current) {
      if (openDrawer) {
        openDrawer();
      }
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [aspenMessages]);

  // If highlightedHint changes, open the drawer
  useEffect(() => {
    if (highlightedHint && openDrawer) {
      openDrawer();
    }
  }, [highlightedHint, openDrawer]);

  return (
    <div className={S.container}>
      <div className={S.messages} ref={scrollRef}>
        {hintsLoading ? (
          <AspenMessage message="Loading hints..." />
        ) : hintsError ? (
          <AspenMessage message="Unable to load hints. Please try again later." />
        ) : aspenMessages.length === 0 ? (
          <AspenMessage message={NoHintsMessage} />
        ) : (
          <>
            <ul className={S.hintsList}>
              {aspenMessages.map((message, idx) => (
                <li key={message.runId} className={S.hintItem}>
                  {idx > 0 && <Divider key={`divider-${idx}`} className={S.divider} />}
                  <AspenMessage
                    runId={message.runId}
                    message={message.text}
                    feedback={message.feedback}
                    highlighted={message.runId === highlightedHint?.submissionId}
                    highlightTime={highlightedHint?.timestamp}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default HintsPanel;
